import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  RestartAlt as RestartAltIcon,
  HelpOutline as HelpOutlineIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import WellsCharts from '../charts/WellsCharts';
import FullScreenTable from '../tables/FullScreenTable';
import UpgradeRequestDialog from '../UpgradeRequestDialog';
import logger from '../../utils/logger';

const WellsTab = ({
  searchData,
  getCurrentTableCounts,
  getCurrentTableData,
  getCurrentTableType,
  wellGroupingTableSort,
  setWellGroupingTableSort,
  handleWellGroupingTableSort,
  riskWellsTableSort,
  setRiskWellsTableSort,
  handleRiskWellsTableSort,
  multipleRecordsTableSort,
  setMultipleRecordsTableSort,
  handleMultipleRecordsTableSort,
  resetMultipleRecordsTableSort,
  riskWellsFilter,
  setRiskWellsFilter,
  resetRiskWellsFilter,
  inactiveYearsBinSize,
  setInactiveYearsBinSize,
  resetInactiveYearsBinSize,
  hiddenWellGroups,
  setHiddenWellGroups,
  resetHiddenWellGroups,
  tierInfo,
}) => {
  // Local state for operator names popup
  const [operatorNamesDialogOpen, setOperatorNamesDialogOpen] = useState(false);
  // Local state for full screen table
  const [fullScreenTableOpen, setFullScreenTableOpen] = useState(false);
  // Local state for about dialog
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  // Local state for upgrade dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  // Reset function for risk wells table
  const handleResetRiskWells = () => {
    setRiskWellsTableSort({ field: 'inactive_years', direction: 'desc' });
  };

  // Reset function for multiple records table
  const handleResetMultipleRecords = () => {
    resetMultipleRecordsTableSort();
  };

  // Sort function for multiple records table
  const handleMultipleRecordsSort = (field) => {
    handleMultipleRecordsTableSort(field);
  };
  
  // Wells-specific data and configuration
  const wellsData = getCurrentTableData();
  const allWellsColumns = wellsData.length > 0 ? Object.keys(wellsData[0]) : [];
  const defaultWellsColumns = ['operator_name', 'api_number', 'well_name', 'well_group', 'drill_date'];
  
  // Don't render if not wells tab or no data
  if (getCurrentTableType() !== 'wells' || !wellsData || wellsData.length === 0) {
    return null;
  }
  
  // Calculate unique operator names for this tab only
  const getTabOperatorNames = () => {
    if (!wellsData || wellsData.length === 0) return [];
    
    const operatorNames = new Set();
    wellsData.forEach(well => {
      const operator = well.operator_name || well['Operator Name'] || well.operator;
      if (operator && operator.toString().trim() !== '') {
        operatorNames.add(operator.toString().trim());
      }
    });
    
    return Array.from(operatorNames).sort();
  };

  return (
    <>
      {/* Wells Title Card */}
      <Card elevation={0} sx={{ mb: 3, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" component="h2">
              Wells
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
              <Tooltip title="About Wells" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setAboutDialogOpen(true)}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Raw Data" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setFullScreenTableOpen(true)}
                  disabled={tierInfo && !tierInfo.can_view_raw_data}
                  sx={{
                    opacity: (tierInfo && !tierInfo.can_view_raw_data) ? 0.5 : 1,
                    cursor: (tierInfo && !tierInfo.can_view_raw_data) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <TableChartIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
           {/* Content */}
           <Box>
             {/* Unique Operator Names */}
             <Box sx={{ mb: 2, textAlign: 'left' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <Typography>
                   Unique Operator Names — {getTabOperatorNames().length.toLocaleString()}
            </Typography>
            <Tooltip title="View all unique operator names" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => setOperatorNamesDialogOpen(true)}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
             {/* Divider */}
             <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, mt: 2 }} />
             
             {/* Findings Content */}
             <Box sx={{ mt: 2 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
        {(() => {
          const totalRecords = getCurrentTableCounts();
          const wellsData = getCurrentTableData();
          const uniqueWells = new Set(wellsData.map(well => 
            well['API Number'] || well.api_number || well.api || ''
          )).size;
          
          if (totalRecords > uniqueWells) {
            const wellsWithMultipleRecords = totalRecords - uniqueWells;
            return (
              <>
                The well information review identified{' '}
                <strong>{totalRecords.toLocaleString()}</strong> relevant well records and{' '}
                <strong>{uniqueWells.toLocaleString()}</strong> unique well APIs associated with the subject assets{' '}
                ({wellsWithMultipleRecords} well{wellsWithMultipleRecords === 1 ? '' : 's'} with multiple on-schedule API records).
              </>
            );
          } else {
            return (
              <>
                The well information review identified{' '}
                <strong>{totalRecords.toLocaleString()}</strong> wells associated with the subject assets.
              </>
            );
          }
        })()}
      </Typography>

      {/* Total Plugging Cost */}
      <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
        {(() => {
          const wellsData = getCurrentTableData();
          
          // Calculate total plugging cost - sum all numeric PLUGGING_COST values
          const totalPluggingCost = wellsData.reduce((sum, well) => {
            const pluggingCost = well['PLUGGING_COST'] || well['plugging_cost'] || well['Plugging Cost'] || null;
            
            // Only include numeric values (exclude null, undefined, "SEE OTHER API RECORD", etc.)
            if (pluggingCost !== null && pluggingCost !== undefined && pluggingCost !== '' && 
                typeof pluggingCost === 'number' && !isNaN(pluggingCost)) {
              return sum + pluggingCost;
            }
            return sum;
          }, 0);
          
          if (totalPluggingCost > 0) {
            return (
              <>
                Total estimated Asset Retirement Obligation (ARO) at <strong>${totalPluggingCost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>, based on mean cost per vertical foot (see "About Wells" for details).
              </>
            );
          }
          return null;
        })()}
      </Typography>

      {/* Risk Wells Count */}
      {(() => {
        const wellsData = getCurrentTableData();
                 
                 // Helper function to check if well is inactive
                 const isInactiveWell = (well) => {
                   const plugDate = well['Plug Date'] || well.plug_date || well.plugged_date;
                   const wellGroup = well['Well Group'] || well.well_group || well.well_status || well.status || '';
          const inactiveYears = well.inactive_years || 0;
                   const wellGroupUpper = wellGroup.toString().toUpperCase();
                   
                   // Must be unplugged
                   const isUnplugged = plugDate === null || plugDate === undefined || plugDate === '';
                   
                   // Check if Active or Permit
                   const isActiveOrPermit = wellGroupUpper === 'ACTIVE' || wellGroupUpper === 'PERMIT';
                   
                   // Inactive if:
                   // 1. Unplugged AND not Active/Permit (standard inactive wells)
                   // OR
                   // 2. Active/Permit BUT has inactive_years > 0 (should be considered inactive)
                   return (isUnplugged && !isActiveOrPermit) || (isActiveOrPermit && inactiveYears > 0);
                 };
        
                 const allInactiveWells = wellsData.filter(well => isInactiveWell(well));
                 
                 const highRiskWells = allInactiveWells.filter(well => {
          const inactiveYears = well.inactive_years || 0;
                   return inactiveYears > 5;
        });
        
        // Helper function to calculate plugging cost for a well
        const getPluggingCost = (well) => {
          const pluggingCost = well['PLUGGING_COST'] || well['plugging_cost'] || well['Plugging Cost'] || null;
          if (pluggingCost !== null && pluggingCost !== undefined && pluggingCost !== '' && 
              typeof pluggingCost === 'number' && !isNaN(pluggingCost)) {
            return pluggingCost;
          }
          return 0;
        };
        
        // Calculate ARO for all inactive wells
        const allInactiveARO = allInactiveWells.reduce((sum, well) => sum + getPluggingCost(well), 0);
        
        // Calculate ARO for high risk wells (inactive for over 5 years)
        const highRiskARO = highRiskWells.reduce((sum, well) => sum + getPluggingCost(well), 0);
        
        return (
          <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
            {(() => {
              const allInactiveRecords = allInactiveWells.length;
              const allInactiveUniqueWells = new Set(allInactiveWells.map(well => 
                well['API Number'] || well.api_number || well.api || ''
              )).size;
              
              if (allInactiveRecords > allInactiveUniqueWells) {
                return (
                  <>
                    The review further identified <strong>{allInactiveRecords.toLocaleString()}</strong> inactive well records (ARO of <strong>${allInactiveARO.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>) (<strong>{allInactiveUniqueWells.toLocaleString()}</strong> unique APIs), with <strong>{highRiskWells.length.toLocaleString()}</strong> inactive for over 5 years (ARO of <strong>${highRiskARO.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>).
                  </>
                );
              } else {
                return (
                  <>
                    The review further identified <strong>{allInactiveRecords.toLocaleString()}</strong> inactive well records (ARO of <strong>${allInactiveARO.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>), with <strong>{highRiskWells.length.toLocaleString()}</strong> inactive for over 5 years (ARO of <strong>${highRiskARO.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>).
                  </>
                );
              }
            })()}
          </Typography>
        );
      })()}

      {/* Analytics Scope Notice */}
      {getCurrentTableData().length < getCurrentTableCounts() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Analytics are based on {getCurrentTableData().length.toLocaleString()} of {getCurrentTableCounts().toLocaleString()} identified records 
            (limited to first {getCurrentTableData().length.toLocaleString()} rows).
          </Typography>
        </Alert>
      )}

      {/* Well Grouping Table */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Well Grouping Statistics
        </Typography>
        <Tooltip title="Reset Table" arrow>
          <IconButton
            size="small"
            color="primary"
            onClick={() => setWellGroupingTableSort({ field: '', direction: 'asc' })}
          >
            <RestartAltIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }} data-table-title="Well Grouping Statistics" data-table-type="findings">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleWellGroupingTableSort('name')}
              >
                Well Group
                {wellGroupingTableSort.field === 'name' && (
                  <Box component="span" sx={{ ml: 1 }}>
                    {wellGroupingTableSort.direction === 'asc' ? '↑' : '↓'}
                  </Box>
                )}
              </TableCell>
              <TableCell 
                align="right" 
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleWellGroupingTableSort('count')}
              >
                Count
                {wellGroupingTableSort.field === 'count' && (
                  <Box component="span" sx={{ ml: 1 }}>
                    {wellGroupingTableSort.direction === 'asc' ? '↑' : '↓'}
                  </Box>
                )}
              </TableCell>
              <TableCell 
                align="right" 
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleWellGroupingTableSort('percentage')}
              >
                % of Total
                {wellGroupingTableSort.field === 'percentage' && (
                  <Box component="span" sx={{ ml: 1 }}>
                    {wellGroupingTableSort.direction === 'asc' ? '↑' : '↓'}
                  </Box>
                )}
              </TableCell>
              <TableCell 
                align="right" 
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleWellGroupingTableSort('inactiveCount')}
              >
                Inactive Count
                {wellGroupingTableSort.field === 'inactiveCount' && (
                  <Box component="span" sx={{ ml: 1 }}>
                    {wellGroupingTableSort.direction === 'asc' ? '↑' : '↓'}
                  </Box>
                )}
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleWellGroupingTableSort('inactiveYears')}
              >
                Avg Inactive Years (&gt;0)
                {wellGroupingTableSort.field === 'inactiveYears' && (
                  <Box component="span" sx={{ ml: 1 }}>
                    {wellGroupingTableSort.direction === 'asc' ? '↑' : '↓'}
                  </Box>
                )}
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => handleWellGroupingTableSort('epc')}
              >
                ARO $
                {wellGroupingTableSort.field === 'epc' && (
                  <Box component="span" sx={{ ml: 1 }}>
                    {wellGroupingTableSort.direction === 'asc' ? '↑' : '↓'}
                  </Box>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const totalWells = getCurrentTableCounts();
              const wellsData = getCurrentTableData();
              
              // Group wells by status and calculate real statistics
              const wellGroups = {};
              
              // Well Group color scheme for table borders
              const wellGroupColors = {
                         'ACTIVE': '#4caf50',
                         'PERMIT': '#607d8b',
                         'SHUT IN': '#ffc107',
                         'TA': '#2196f3',
                         'PARTIAL PLUG': '#ff9800',
                         'P&A': '#8d6e63',
                         'DRY HOLE': '#e91e63'
              };
              
              // Try different possible column names for Well Group
              const wellGroupColumn = wellsData.length > 0 ? 
                (wellsData[0]['Well Group'] !== undefined ? 'Well Group' :
                 wellsData[0].well_group !== undefined ? 'well_group' :
                 wellsData[0].well_status !== undefined ? 'well_status' :
                 wellsData[0].status !== undefined ? 'status' : null) : null;
              
              if (!wellGroupColumn) {
                return (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                        No well group data available
                    </TableCell>
                  </TableRow>
                );
              }
              
              wellsData.forEach(well => {
                const status = well[wellGroupColumn] || 'UNKNOWN';
                const inactiveYears = well.inactive_years || 0;
                
                // Use the same isInactiveWell logic as the summary text
                const plugDate = well['Plug Date'] || well.plug_date || well.plugged_date;
                const wellGroup = well[wellGroupColumn] || '';
                const wellGroupUpper = wellGroup.toString().toUpperCase();
                const isUnplugged = plugDate === null || plugDate === undefined || plugDate === '';
                const isActiveOrPermit = wellGroupUpper === 'ACTIVE' || wellGroupUpper === 'PERMIT';
                const isInactive = (isUnplugged && !isActiveOrPermit) || (isActiveOrPermit && inactiveYears > 0);
                
                if (!wellGroups[status]) {
                  wellGroups[status] = {
                    count: 0,
                    inactiveCount: 0,
                    totalInactiveYears: 0,
                    totalEPC: 0
                  };
                }
                
                wellGroups[status].count++;
                
                // Calculate EPC for this well
                const pluggingCost = well['PLUGGING_COST'] || well['plugging_cost'] || well['Plugging Cost'] || null;
                if (pluggingCost !== null && pluggingCost !== undefined && pluggingCost !== '' && 
                    typeof pluggingCost === 'number' && !isNaN(pluggingCost)) {
                  wellGroups[status].totalEPC += pluggingCost;
                }
                
                // Count inactive wells using the same logic as summary text
                if (isInactive) {
                  wellGroups[status].inactiveCount++;
                  // Only add to totalInactiveYears if we have a value
                  if (inactiveYears > 0) {
                    wellGroups[status].totalInactiveYears += inactiveYears;
                  }
                }
              });
              
              const tableData = Object.entries(wellGroups).map(([status, data]) => ({
                name: status,
                count: data.count,
                percentage: (data.count / totalWells) * 100,
                inactiveCount: data.inactiveCount,
                inactiveYears: data.inactiveCount > 0 ? data.totalInactiveYears / data.inactiveCount : 0,
                epc: data.totalEPC,
                color: wellGroupColors[status] || '#9e9e9e'
              }));
              
              // Apply sorting if specified, otherwise sort by predefined Well Group order
              if (wellGroupingTableSort.field) {
                tableData.sort((a, b) => {
                  let aVal = a[wellGroupingTableSort.field];
                  let bVal = b[wellGroupingTableSort.field];
                  
                  if (wellGroupingTableSort.direction === 'desc') {
                    [aVal, bVal] = [bVal, aVal];
                  }
                  
                  if (typeof aVal === 'string') {
                    return aVal.localeCompare(bVal);
                  }
                  return aVal - bVal;
                });
              } else {
                // Default sort by predefined Well Group order
                const wellGroupOrder = ['ACTIVE', 'PERMIT', 'SHUT IN', 'TA', 'PARTIAL PLUG', 'P&A', 'DRY HOLE'];
                tableData.sort((a, b) => {
                  const aIndex = wellGroupOrder.indexOf(a.name);
                  const bIndex = wellGroupOrder.indexOf(b.name);
                  
                  // If both are in the predefined order, sort by their position
                  if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                  }
                  
                  // If only one is in the predefined order, prioritize it
                  if (aIndex !== -1) return -1;
                  if (bIndex !== -1) return 1;
                  
                  // If neither is in the predefined order, sort alphabetically
                  return a.name.localeCompare(b.name);
                });
              }
              
              let grandTotalCount = 0;
              let grandTotalInactive = 0;
              let totalInactiveYears = 0;
              let grandTotalEPC = 0;
              
              // Calculate grand totals
              tableData.forEach(row => {
                grandTotalCount += row.count;
                grandTotalInactive += row.inactiveCount;
                totalInactiveYears += row.inactiveCount * row.inactiveYears;
                grandTotalEPC += row.epc;
              });
              
              // Create table rows
              const tableRows = tableData.map((row) => (
                <TableRow key={row.name}>
                  <TableCell>{row.name === 'TA' ? 'TA' : row.name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}</TableCell>
                  <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.percentage.toFixed(1)}%</TableCell>
                  <TableCell align="right">{row.inactiveCount > 0 ? row.inactiveCount.toLocaleString() : '-'}</TableCell>
                  <TableCell align="right">{row.inactiveYears > 0 ? `${row.inactiveYears.toFixed(1)} years` : '-'}</TableCell>
                  <TableCell align="right">{row.epc > 0 ? `$${row.epc.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '-'}</TableCell>
                </TableRow>
              ));
              
              // Add grand total row
              tableRows.push(
                <TableRow key="grand-total" sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Grand Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{grandTotalCount.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>100.0%</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{grandTotalInactive > 0 ? grandTotalInactive.toLocaleString() : '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {grandTotalInactive > 0 ? `${(totalInactiveYears / grandTotalInactive).toFixed(1)} years` : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {grandTotalEPC > 0 ? `$${grandTotalEPC.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '-'}
                  </TableCell>
                </TableRow>
              );
              
              return tableRows;
            })()}
          </TableBody>
        </Table>
      </TableContainer>
             </Box>
           </Box>
           
           {tierInfo && !tierInfo.can_view_raw_data && (
             <Typography variant="body2" color="primary.main" sx={{ 
               mt: 2, 
               mb: 1, 
               fontWeight: 500 
             }}>
               💡 <button 
                 onClick={() => setUpgradeDialogOpen(true)}
                 style={{ 
                   color: 'inherit', 
                   textDecoration: 'underline',
                   cursor: 'pointer',
                   background: 'none',
                   border: 'none',
                   padding: 0,
                   font: 'inherit'
                 }}
               >
                 Upgrade to Pro to view raw data tables
               </button>
             </Typography>
           )}
        </CardContent>
      </Card>

      {/* Wells Charts */}
      <WellsCharts
        wellsData={getCurrentTableData()}
        onSortRiskWells={handleRiskWellsTableSort}
        riskWellsSortField={riskWellsTableSort.field}
        riskWellsSortDirection={riskWellsTableSort.direction}
        onResetRiskWells={handleResetRiskWells}
        riskWellsFilter={riskWellsFilter}
        setRiskWellsFilter={setRiskWellsFilter}
        resetRiskWellsFilter={resetRiskWellsFilter}
        inactiveYearsBinSize={inactiveYearsBinSize}
        setInactiveYearsBinSize={setInactiveYearsBinSize}
        resetInactiveYearsBinSize={resetInactiveYearsBinSize}
        hiddenWellGroups={hiddenWellGroups}
        setHiddenWellGroups={setHiddenWellGroups}
        resetHiddenWellGroups={resetHiddenWellGroups}
      />

      {/* Multiple On-Schedule Records by API Table */}
      {(() => {
        const wellsData = getCurrentTableData();
        const apiCounts = {};
        
        // Count records per API
        wellsData.forEach(well => {
          const api = well['API Number'] || well.api_number || well.api || '';
          if (api) {
            apiCounts[api] = (apiCounts[api] || 0) + 1;
          }
        });
        
        // Find APIs with multiple records
        const multipleRecordApis = Object.keys(apiCounts).filter(api => apiCounts[api] > 1);
        
        if (multipleRecordApis.length === 0) return null;
        
        // Get all records for APIs with multiple records
        const multipleRecordWells = wellsData.filter(well => {
          const api = well['API Number'] || well.api_number || well.api || '';
          return multipleRecordApis.includes(api);
        });

        // Sort the multiple record wells
        const sortedMultipleRecordWells = [...multipleRecordWells].sort((a, b) => {
          let aValue, bValue;
          
          switch (multipleRecordsTableSort.field) {
            case 'well_name':
              aValue = a['Well Name'] || a.well_name || a.name || '';
              bValue = b['Well Name'] || b.well_name || b.name || '';
              break;
            case 'api_number':
              aValue = a['API Number'] || a.api_number || a.api || '';
              bValue = b['API Number'] || b.api_number || b.api || '';
              break;
            case 'lease_number':
              aValue = a['Lease Number'] || a.lease_number || a.lease || '';
              bValue = b['Lease Number'] || b.lease_number || b.lease || '';
              break;
            case 'well_group':
              aValue = a['Well Group'] || a.well_group || a.well_status || a.status || '';
              bValue = b['Well Group'] || b.well_group || b.well_status || b.status || '';
              break;
            case 'well_type':
              aValue = a['Well Type'] || a.well_type || a.type || '';
              bValue = b['Well Type'] || b.well_type || b.type || '';
              break;
            default:
              return 0;
          }
          
          // Handle string comparison
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            const comparison = aValue.localeCompare(bValue);
            return multipleRecordsTableSort.direction === 'asc' ? comparison : -comparison;
          }
          
          return 0;
        });
        
        return (
          <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }} data-table-title="Multiple On-Schedule Records by API" data-table-type="main">
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Multiple On-Schedule Records by API
                </Typography>
                <Tooltip title="Reset Table" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleResetMultipleRecords}
                  >
                    <RestartAltIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                      onClick={() => handleMultipleRecordsSort('well_name')}
                    >
                      Well Name
                      {multipleRecordsTableSort.field === 'well_name' && (
                        <Box component="span" sx={{ ml: 1 }}>
                          {multipleRecordsTableSort.direction === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell 
                      sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                      onClick={() => handleMultipleRecordsSort('api_number')}
                    >
                      API Number
                      {multipleRecordsTableSort.field === 'api_number' && (
                        <Box component="span" sx={{ ml: 1 }}>
                          {multipleRecordsTableSort.direction === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell 
                      sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                      onClick={() => handleMultipleRecordsSort('lease_number')}
                    >
                      Lease Number
                      {multipleRecordsTableSort.field === 'lease_number' && (
                        <Box component="span" sx={{ ml: 1 }}>
                          {multipleRecordsTableSort.direction === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell 
                      sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                      onClick={() => handleMultipleRecordsSort('well_group')}
                    >
                      Well Group
                      {multipleRecordsTableSort.field === 'well_group' && (
                        <Box component="span" sx={{ ml: 1 }}>
                          {multipleRecordsTableSort.direction === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell 
                      sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                      onClick={() => handleMultipleRecordsSort('well_type')}
                    >
                      Well Type
                      {multipleRecordsTableSort.field === 'well_type' && (
                        <Box component="span" sx={{ ml: 1 }}>
                          {multipleRecordsTableSort.direction === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedMultipleRecordWells.map((well, index) => (
                    <TableRow key={index}>
                      <TableCell>{well['Well Name'] || well.well_name || well.name || ''}</TableCell>
                      <TableCell>{well['API Number'] || well.api_number || well.api || ''}</TableCell>
                      <TableCell>{well['Lease Number'] || well.lease_number || well.lease || ''}</TableCell>
                      <TableCell>{well['Well Group'] || well.well_group || well.well_status || well.status || ''}</TableCell>
                      <TableCell>{well['Well Type'] || well.well_type || well.type || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })()}

      {/* About Dialog */}
      <Dialog
        open={aboutDialogOpen}
        onClose={() => setAboutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          About Wells
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography paragraph>
              Well information in this dataset is aggregated from various regulatory filings to provide a comprehensive well record.
            </Typography>
            <Typography paragraph sx={{ mt: 3 }}>
              <strong>Key well information</strong> – Includes API numbers, operator information, and lease details used for regulatory tracking. 
              Covers critical dates (permit, drilling, completion, plugging), well description, and location. 
              Provides the current status of the well, including well type and classification group.
            </Typography>
            <Typography paragraph sx={{ ml: 3 }}>
              <strong>Well Group</strong> – Our enhanced well grouping that simplifies state regulatory categories into fewer, key classifications.
            </Typography>
            <Typography paragraph sx={{ ml: 3 }}>
              <strong>Inactive Years</strong> – Our calculated number of years (including partial years) that a well has been inactive. Inactive years are assigned when a well's inactive period reaches or exceeds 1 year.
            </Typography>
            <Typography paragraph sx={{ mt: 3 }}>
              <strong>ARO (Asset Retirement Obligation)</strong> – Estimated Plugging and Abandonment (P&A) costs calculated using district-specific costs per foot multiplied by well vetical depth.
            </Typography>
            <Typography paragraph sx={{ ml: 3 }}>
              <strong>Calculation</strong> – Plugging Cost = District Mean Cost per Foot × API DEPTH (feet). District costs range from $5.16/ft to $27.94/ft, based on Railroad Commission of Texas (RRC) P&A cost data averages for fiscal years 2020-2024.
            </Typography>
            <Typography paragraph sx={{ ml: 3 }}>
              <strong>Excluded Wells</strong> – Wells with Well Group designations as 'PERMIT', 'P&A', or 'DRY HOLE'; and wells with a known Plug Date.
            </Typography>
            <Typography paragraph sx={{ ml: 3, fontSize: '0.875rem', fontStyle: 'italic', color: 'text.secondary' }}>
              Note: If the district cannot be determined for a well, the calculation uses state-wide mean cost of $12.28/ft. 
              For APIs with multiple records, only one selected record receives a cost calculation. Default depth assumptions and district-level averages may not reflect well-specific conditions.
              These estimates should not be used for definitive asset retirement obligations and financial planning.
            </Typography>
            <Typography paragraph sx={{ ml: 3, fontSize: '0.875rem', fontStyle: 'italic', color: 'text.secondary' }}>
              * If API DEPTH data is missing, defaults to 5,000 feet.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operator Names Dialog */}
      <Dialog
        open={operatorNamesDialogOpen}
        onClose={() => setOperatorNamesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Unique Operator Names
            </Typography>
            <IconButton
              onClick={() => setOperatorNamesDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing unique operator names from current search results ({getCurrentTableData().length.toLocaleString()} records)
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {getTabOperatorNames().length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total: {getTabOperatorNames().length.toLocaleString()} unique operators</strong>
                </Typography>
                <List dense>
                  {getTabOperatorNames().map((operator, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText primary={operator} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography>No operator data available</Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Full Screen Table Dialog */}
      <FullScreenTable
        open={fullScreenTableOpen}
        onClose={() => setFullScreenTableOpen(false)}
        tableType="wells"
        data={wellsData}
        allColumns={allWellsColumns}
        defaultColumns={defaultWellsColumns}
        totalCount={getCurrentTableCounts()}
        rowLimit={tierInfo?.table_limits?.wells || 500}
        onReset={() => {
          // Reset any wells-specific state if needed
          logger.debug('Wells table reset');
        }}
      />
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </>
  );
};

export default WellsTab;